import { useEffect, useRef } from 'react';
import {
  decodeServerBinary,
  defaultConfig,
  driveBots,
  encodeDelta,
  encodeKeyframe,
  findSkin,
  findTheme,
  GameEngine,
  StateMirror,
  type DeltaMsg,
  type KeyframeMsg,
} from '@snakefeast/shared';
import { Renderer } from '../game/renderer';
import { prefersReducedMotion } from '../lib/platform';
import { useSettings } from '../store/settings';

// Two Legendary skins among them, so the first thing a visitor sees includes the fancy ones in action.
const BOTS = [
  { name: 'Viper', skin: 'dragon' },
  { name: 'Mamba', skin: 'sunset' },
  { name: 'Cobra', skin: 'thunder' },
  { name: 'Boa', skin: 'grape' },
  { name: 'Python', skin: 'lemon' },
];

/**
 * A real bot match on the real engine and renderer, behind the app's start
 * screen (the web landing shows a recorded clip, HeroClip). Runs through the same binary encode/decode path as online play, pauses
 * when off-screen or the tab is hidden, and renders a single still frame under
 * reduced motion.
 */
export function ArenaPreview() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const theme = useSettings((s) => s.theme);
  const quality = useSettings((s) => s.quality);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const reduce = prefersReducedMotion() || useSettings.getState().reduceMotion;
    const renderer = new Renderer(canvas, {
      theme: findTheme(theme),
      quality: quality === 'low' ? 'low' : 'high',
      reduceMotion: reduce,
      minCellPx: 3,
      labels: false,
    });
    renderer.setPlayers(BOTS.map((b, slot) => ({ slot, name: b.name, team: 0, skin: findSkin(b.skin), isMe: false })));

    let engine!: GameEngine;
    const mirror = new StateMirror();
    const reset = () => {
      engine = new GameEngine(
        defaultConfig(BOTS.length, { countdownTicks: 0, durationTicks: 12 * 90 }),
        BOTS.map((b, slot) => ({ slot, userId: b.name, name: b.name, team: 0, skin: b.skin, isBot: true })),
      );
      mirror.applyKeyframe(decodeServerBinary(encodeKeyframe(engine)) as KeyframeMsg);
      renderer.setGrid(engine.config.width, engine.config.height);
    };
    reset();

    let raf = 0;
    let last = 0;
    let acc = 0;
    let visible = true;
    let restartAt = 0;

    const step = () => {
      driveBots(engine, 'hard');
      const ev = engine.step();
      const before = new Map(mirror.snakes.map((s) => [s.slot, s.alive]));
      const delta = decodeServerBinary(encodeDelta(engine, ev)) as DeltaMsg;
      mirror.applyDelta(delta);
      for (const s of mirror.snakes) {
        if (before.get(s.slot) && !s.alive) renderer.emitDeath(s.prevBody, findSkin(BOTS[s.slot]!.skin).head, false);
      }
      for (const d of delta.deaths) {
        const sig = d.killedBy !== null && d.killedBy !== d.slot ? findSkin(BOTS[d.killedBy]!.skin).signature : undefined;
        const victim = mirror.snakes.find((s) => s.slot === d.slot);
        if (sig && victim) renderer.emitKill(victim.prevBody, sig);
      }
      for (const e of ev.eaten) renderer.emitBurst(e.cell, findSkin(BOTS[e.slot]!.skin).head, 6);
    };

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      if (!visible) {
        last = now;
        return;
      }
      const dt = last ? Math.min(100, now - last) : 0;
      last = now;
      if (engine.isFinished()) {
        restartAt ||= now + 1500;
        if (now >= restartAt) {
          restartAt = 0;
          reset();
        }
      } else {
        acc += dt;
        while (acc >= engine.tickMs) {
          acc -= engine.tickMs;
          step();
        }
      }
      renderer.render(mirror, Math.min(1, acc / engine.tickMs), now, null, null);
    };

    const ro = new ResizeObserver(() => {
      renderer.resize();
      if (reduce) renderer.render(mirror, 1, 0, null, null);
    });
    ro.observe(canvas);
    const io = new IntersectionObserver(([e]) => (visible = !!e?.isIntersecting && !document.hidden));
    io.observe(canvas);
    const onVis = () => (visible = !document.hidden);
    document.addEventListener('visibilitychange', onVis);

    if (reduce) {
      // A representative still frame: fast-forward a few seconds of play, draw once.
      for (let i = 0; i < 60; i++) step();
      renderer.resize();
      renderer.render(mirror, 1, 0, null, null);
    } else {
      raf = requestAnimationFrame(frame);
    }

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [theme, quality]);

  return <canvas ref={canvasRef} className="preview-canvas" aria-hidden="true" />;
}
